import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "react-query";
import { format } from "date-fns";
import {
  BackIcon,
  CloseIcon,
  DiscardIcon,
  DownloadIcon,
  EditIcon,
  FolderIcon,
  FoldersIcon,
  HamburderIcon,
  MenuDeleteIcon,
  MenuDownloadIcon,
  RenameIcon,
  RestoreFoldrIcon,
  RestoreIcon,
  SearchIcon,
  ViewIcon,
  WhiteFolderIcon,
} from "assets/images";
import {
  Button,
  HeadwallImage,
  Input,
  Pagination,
  Select,
  Tooltip,
} from "components";
import { useHeadWallStore, useLoaderStore, useRightPanelStore } from "store";
import {
  deleteAlert,
  errorAlert,
  removeFromFolderAlert,
  successAlert,
} from "config/sweet-alert/alert";
import {
  createFolder,
  downloadFolderDesigns,
  getFolders,
  moveFilesToFolder,
  renameFolder,
  requestDesigns,
  downloadDesign,
  filterDesigns,
  trashFolder,
  trashDesigns,
  deleteDesign,
  clearTrash,
  deleteFolder,
  getTrashedFolders,
  restoreTrashedItem,
} from "services/dashboardServices";
import useDebounce from "hooks/useDebounce";

const models = {
  infinity: "Horizontal Infinity",
  axiom: "Axiom Headwall",
  array: "Array SSH Headwall",
  compass: "Compass Headwall",
  console: "Console Headwall",
  eloquence: "Eloquence Headwall",
  security_console: "Security Headwall",
  hidden: "Hidden Headwall",
};

const heawallType = [
  { label: "All", value: "all" },
  { label: "Horizontal Infinity", value: "infinity" },
  { label: "Axiom Headwall", value: "axiom" },
  { label: "Array SSH Headwall", value: "array" },
  { label: "Compass Headwall", value: "compass" },
  { label: "Console Headwall", value: "console" },
  { label: "Eloquence Headwall", value: "eloquence" },
  { label: "Security Headwall", value: "security_console" },
  { label: "Hidden Headwall", value: "hidden" },
];

function DesignList({ limit, heading, showPagination, isDeletePage }) {
  const { setLoading } = useLoaderStore();
  const { resetState } = useHeadWallStore();
  const { updateStepCompletion } = useRightPanelStore();
  const [showImage, setShowImage] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showRenamefolderDialog, setShowRenamefolderDialog] = useState({
    open: false,
    id: "",
  });
  const [showCreatefolderDialog, setShowCreatefolderDialog] = useState(false);
  const [openFolder, setOpenFolder] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedDesigns, setSelectedDesigns] = useState([]);
  const [folders, setFolders] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [headwall_type, setHeadwallType] = useState("all");
  // ref
  const dragItem = useRef();

  const navigate = useNavigate();
  const debouncedQuery = useDebounce(searchText, 500);

  const foldersQuery = useQuery({
    queryKey: ["folders"],
    refetchOnWindowFocus: false,
    queryFn: () => (isDeletePage ? getTrashedFolders() : getFolders()),
    onSuccess: (data) => {
      setFolders(data.data.data);
    },
  });

  const trashFolderMutation = useMutation(trashFolder, {
    onSuccess: (data) => {
      successAlert("Ok", data.data.message);
      setSelectedDesigns([]);
      foldersQuery.refetch();
      searchMutation.mutate({
        page,
        searchText: searchText.trim(),
        model: headwall_type,
        limit: 15,
        folder_id: selectedFolder,
        is_trashed: isDeletePage,
      });
    },
    onError: (err) => {
      errorAlert(err.response.data.message);
    },
  });

  const deleteFolderMutation = useMutation(deleteFolder, {
    onSuccess: (data) => {
      successAlert("Ok", data.data.message);
      setSelectedDesigns([]);
      foldersQuery.refetch();
      handleSearch();
    },
    onError: (err) => {
      errorAlert(err.response.data.message);
    },
  });

  const trashDesignsMutation = useMutation(trashDesigns, {
    onSuccess: (data) => {
      successAlert("Ok", data.data.message);
      if (showImage) setShowImage(false);
      let newPage = selectAll ? (page === 0 ? 0 : page - 1) : page;
      setPage(newPage);
      if (selectedFolder !== null) {
        searchMutation.mutate({
          page: newPage,
          searchText: searchText.trim(),
          model: headwall_type,
          limit: 15,
          folder_id: selectedFolder,
          is_trashed: isDeletePage,
        });
      } else handleSearch();
      setSelectedDesigns([]);
      if (selectAll) setSelectAll(false);
    },
    onError: (err) => {
      errorAlert(err.response.data.message);
    },
  });

  const deleteDesignsMutation = useMutation(deleteDesign, {
    onSuccess: (data) => {
      successAlert("Ok", data.data.message);
      handleSearch();
      setSelectedDesigns([]);
      if (selectAll) setSelectAll(false);
    },
    onError: (err) => {
      errorAlert(err.response.data.message);
    },
  });

  const clearTrashMutation = useMutation(clearTrash, {
    onSuccess: (data) => {
      successAlert("Ok", data.data.message);
      foldersQuery.refetch();
      handleSearch(); //fetch designs
      // fetch folders
      setSelectedDesigns([]);
      if (selectAll) setSelectAll(false);
    },
    onError: (err) => {
      errorAlert(err.response.data.message);
    },
  });

  const requestDesignsMutation = useMutation(requestDesigns, {
    onSuccess: (data) => {
      successAlert("Ok", data.data.message);
    },
    onError: (err) => {
      errorAlert(err.response.data.message);
    },
  });

  const renameFolderMutation = useMutation(renameFolder, {
    onSuccess: (data) => {
      successAlert("Ok", data.data.message);
      setShowRenamefolderDialog({ open: false, id: "" });
      foldersQuery.refetch();
    },
    onError: (err) => {
      errorAlert(err.response.data.message);
    },
  });

  const moveToFolderMutation = useMutation({
    mutationFn: moveFilesToFolder,
    onSuccess: (data) => {
      successAlert("Ok", data.data.message);
      showImage && handleCloseViewDesign();
      let newPage = selectAll ? (page === 0 ? 0 : page - 1) : page;
      setPage(newPage);
      searchMutation.mutate({
        page: newPage,
        searchText: searchText.trim(),
        model: headwall_type,
        limit: 15,
        folder_id: selectedFolder,
        is_trashed: isDeletePage,
      });
      if (selectAll) setSelectAll(false);
    },
    onError: (err) => {
      errorAlert(err.response.data.message);
    },
  });

  //function mutation
  const createNewFolderMutation = useMutation({
    mutationFn: createFolder,
    onSuccess: (data) => {
      successAlert("Ok", data.data.message);
      foldersQuery.refetch();
      setShowCreatefolderDialog(false);
    },
    onError: (err) => {
      errorAlert(err.response.data.message);
    },
  });

  //function mutation
  const searchMutation = useMutation({
    queryKey: ["search-designs"],
    mutationFn: filterDesigns,
    onSuccess: (data) => {
      setDesigns(data.data.data);
      setPages(Math.ceil(data.data.count / 15));
    },
    onError: (err) => {
      setDesigns([]);
    },
  });

  //function

  const restoreMutation = useMutation({
    mutationFn: restoreTrashedItem,
    onSuccess: (data) => {
      console.log("test");
      successAlert("Ok", data.data.message);
      let newPage = selectAll ? (page === 0 ? 0 : page - 1) : page;
      setPage(newPage);
      searchMutation.mutate({
        page: newPage,
        searchText: searchText.trim(),
        model: headwall_type,
        limit: 15,
        folder_id: selectedFolder,
        is_trashed: isDeletePage,
      });
      foldersQuery.refetch();
      if (selectAll) setSelectAll(false);
    },
    onError: (err) => {
      errorAlert(err.response.data.message);
    },
  });

  useEffect(() => {
    searchMutation.mutate({
      page,
      searchText: searchText.trim(),
      model: headwall_type,
      limit: 15,
      folder_id: selectedFolder,
      is_trashed: isDeletePage,
    });
  }, [headwall_type, page]); // <- depends on all

  useEffect(() => {
    if (debouncedQuery) {
      // eslint-disable-next-line
      handleSearch();
    }
  }, [debouncedQuery]);

  //manage loader
  useEffect(() => {
    const loading =
      // isFetching ||
      foldersQuery.isFetching ||
      moveToFolderMutation.isLoading ||
      // fetchFolderDesignsQuery.isFetching ||
      deleteFolderMutation.isLoading ||
      trashFolderMutation.isLoading ||
      createNewFolderMutation.isLoading ||
      renameFolderMutation.isLoading ||
      requestDesignsMutation.isLoading ||
      searchMutation.isLoading;
    setLoading(loading);
    return () => {
      setLoading(false);
    };
  }, [
    // isFetching,
    foldersQuery.isFetching,
    deleteFolderMutation.isLoading,
    trashFolderMutation.isLoading,
    // fetchFolderDesignsQuery.isFetching,
    createNewFolderMutation.isLoading,
    moveToFolderMutation.isLoading,
    renameFolderMutation.isLoading,
    requestDesignsMutation.isLoading,
    setLoading,
    searchMutation.isLoading,
  ]);

  // // Close the menu if the user clicks outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".folder-item")) {
        setOpenFolder(null); // Close menu if clicked outside
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearch = useCallback(() => {
    if (searchText.trim() || headwall_type) {
      const values = {
        page: 0,
        searchText: searchText.trim(),
        limit: 15,
        folder_id: selectedFolder,
        model: headwall_type,
        is_trashed: isDeletePage,
      };
      searchMutation.mutate(values);
    }
  }, [page, searchText, headwall_type]);

  const formatDate = (d) => {
    const date = new Date(d);

    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };

    const formattedDate = date
      .toLocaleDateString("en-IN", options)
      .replace(/at/gi, "") // Remove 'at'
      .replace(/\b(am|pm)\b/gi, (match) => match.toUpperCase()); // Capitalize 'am' or 'pm';
    return formattedDate;
  };

  const handleDownloadDesign = async (fileURL, name, model, date) => {
    try {
      const newUrl = fileURL.split("/");

      const response = await downloadDesign(newUrl[newUrl.length - 1], name);
      // Convert the response to a Blob (binary data)
      const blob = await response.blob();

      // Get the filename from the Content-Disposition header (you can customize this)
      const newDate = new Date(date);
      const formatted = newDate
        .toISOString()
        .slice(2, 10) // get '25-04-30'
        .replace(/-/g, ""); // remove dashes
      const filename = `${formatted}_${model}_${name}.png`; // You can extract this dynamically if needed

      // Create a temporary URL for the Blob
      const url = window.URL.createObjectURL(blob);

      // Create an anchor element to trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.download = filename; // Specify the filename for the download

      // Append the link to the DOM, click it, and then remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the Blob URL
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const handleEditDesign = (designId) => {
    updateStepCompletion(3, true);
    navigate(`/editor?id=${designId}`);
  };

  const toggleMenu = (e, index) => {
    e.stopPropagation();
    // If the same folder is clicked, close the menu, otherwise open it
    setOpenFolder(index === openFolder ? null : index);
    setSelectedDesigns([]);
  };

  const handleDesignSelect = (design) => {
    let newArr = [...selectedDesigns];
    const checkIndex = selectedDesigns.findIndex(
      (des) => des._id === design._id
    );
    if (checkIndex !== -1) newArr.splice(checkIndex, 1);
    else newArr.push(design);
    if (newArr.length === designs.length) setSelectAll(true);
    else setSelectAll(false);
    setSelectedDesigns(newArr);
  };

  const handleRename = (e) => {
    e.stopPropagation();
    setShowRenamefolderDialog({ open: true, id: openFolder });
    setOpenFolder(null);
    // Perform rename action here
  };

  const handleRequestCADQuote = (requestType) => {
    const values = {
      designIds: selectedDesigns,
      quote_requested:
        requestType === "quote" ? true : requestType === "both" ? true : false, // true or false
      cad_requested:
        requestType === "cad" ? true : requestType === "both" ? true : false, // true or false
    };
    requestDesignsMutation.mutate(values);
    // Perform CAD request action here
  };

  const handleDelete = (e, prop) => {
    e.stopPropagation();
    const header =
      prop === "file"
        ? "Are you sure you want to delete your design?"
        : "Are you sure you want to delete this folder?";
    const message =
      prop === "file"
        ? "Please note that the entire design(s) will be moved to Trash. You can restore it later from your Trash designs if needed."
        : "Please note that the entire folder will be moved to Trash. You can restore it later from your Trash designs if needed.";
    deleteAlert(
      prop === "file" ? handleDiscardDesigns : handleDiscardFolder,
      header,
      message,
      "Yes, Delete"
    );
    setOpenFolder(null);
  };

  const handleDiscardFolder = () => {
    isDeletePage
      ? deleteFolderMutation.mutate(openFolder)
      : trashFolderMutation.mutate(openFolder);
  };

  const handleDiscardDesigns = () => {
    const idsArr = selectedDesigns.map((files) => files._id);
    isDeletePage
      ? deleteDesignsMutation.mutate(idsArr)
      : trashDesignsMutation.mutate(idsArr);
  };

  const handleCloseViewDesign = () => {
    setShowImage(false);
    setSelectedDesigns([]);
    if (selectAll) setSelectAll(false);
    handleSearch();
  };

  const handleRenameFolder = (values) => {
    if (showCreatefolderDialog) {
      delete values.folderId;
      createNewFolderMutation.mutate(values);
    } else renameFolderMutation.mutate(values);
  };

  const handleDownloadFolderZip = async (e, folder) => {
    try {
      setLoading(true);
      e.stopPropagation();
      const response = await downloadFolderDesigns(folder._id);
      // Convert the response to a Blob (binary data)
      const blob = await response.blob();

      // Get the filename from the Content-Disposition header (you can customize this)
      const filename = `${folder.name}.zip`; // You can extract this dynamically if needed

      // Create a temporary URL for the Blob
      const url = window.URL.createObjectURL(blob);

      // Create an anchor element to trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.download = filename; // Specify the filename for the download

      // Append the link to the DOM, click it, and then remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the Blob URL
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDesign = (des) => {
    setSelectedDesigns([des]);
    setShowImage(true);
  };

  const _handleEditDesign = (des) => {
    handleEditDesign(des._id);
  };

  const _handleDownloadDesign = (des) => {
    handleDownloadDesign(
      des.design_image_url,
      des.name,
      des.model,
      des.updated_at
    );
  };

  const handleRemoveFromFolder = () => {
    removeFromFolderAlert(
      handleRemoveFileFromFolder,
      "Warning",
      "Are you sure you want to move the design to the dashboard?",
      "Yes, Move"
    );
  };

  const handleRemoveFileFromFolder = () => {
    const idsArr = selectedDesigns.map((files) => files._id);
    const values = {
      fromFolderId: selectedFolder,
      toFolderId: "",
      inFolder: true, // true-false
      designs: idsArr,
    };
    moveToFolderMutation.mutate(values);
    if (!showImage) setSelectedDesigns([]);
  };

  const handleSelectAll = (value) => {
    setSelectAll(value);
    if (value) setSelectedDesigns(designs);
    else setSelectedDesigns([]);
  };

  const handlePageClick = (page) => {
    const { selected } = page;
    setPage(selected);
    if (selectAll) setSelectAll(false);
    setSelectedDesigns([]);
  };

  const handleDragImage = async (e, item) => {
    dragItem.current = item;
  };

  const handleImageDragOver = (e) => {
    e.preventDefault();
  };

  const handleDropImage = (e, folder) => {
    const desingId = dragItem.current?._id;
    const from = dragItem.current?.folder_id;
    const values = {
      fromFolderId: from,
      toFolderId: folder._id,
      inFolder: from ? true : false, // true-false
      designs: [desingId],
    };
    moveToFolderMutation.mutate(values);
  };

  const handleDeleteTrashedDesigns = (e, prop) => {
    e.stopPropagation();
    const header =
      prop === "file"
        ? "Are you sure you want to delete your design?"
        : "Are you sure you want to delete this folder?";
    const message =
      prop === "file"
        ? "Please keep in mind that design(s) will be deleted permanently and this process cannot be reversible."
        : "Deleting this folder will permanently remove the folder and all its contents, if any. This action cannot be reversible.";
    deleteAlert(
      prop === "file" ? handleDiscardDesigns : handleDiscardFolder,
      header,
      message,
      "Yes, Delete"
    );
    setOpenFolder(null);
  };

  const handleClearTrash = () => {
    clearTrashMutation.mutate();
  };

  const _handleClearTrash = (e) => {
    e.stopPropagation();
    deleteAlert(
      handleClearTrash,
      "Are you sure you want to delete all your designs and folders?",
      "Please keep in mind that entire designs and folders will be deleted permanently and this process cannot be reversible.",
      "Yes, Delete"
    );
    setOpenFolder(null);
  };

  const handleRestoreItems = () => {
    let values;
    const idsArr = selectedDesigns?.map((files) => files._id);
    if (idsArr.length)
      values = {
        action: "design",
        designIds: idsArr,
      };
    else
      values = {
        action: "folder",
        folderId: openFolder,
      };
    restoreMutation.mutate(values);
  };

  const _handleRestoreItems = (e) => {
    e.stopPropagation();
    removeFromFolderAlert(
      handleRestoreItems,
      "Are you sure you want to restore your deleted items?",
      "",
      "Yes, Restore"
    );
    setSelectedDesigns([]);
    setOpenFolder(null);
  };

  const _handleSearch = (e) => {
    if (e.target.value) {
      const pattern = /^[a-zA-Z0-9\s]+$/;
      if (pattern.test(e.target.value)) setSearchText(e.target.value);
    } else {
      setSearchText(e.target.value);
      const values = {
        page,
        searchText: "",
        limit: 15,
        folder_id: selectedFolder,
        model: headwall_type,
        is_trashed: isDeletePage,
      };
      searchMutation.mutate(values);
    }
  };

  const handleClearSearch = () => {
    setSearchText("");
    if (selectAll) setSelectAll(false);
    setSelectedDesigns([]);
    searchMutation.mutate({
      page,
      searchText: "",
      model: headwall_type,
      limit: 15,
      folder_id: selectedFolder,
      is_trashed: isDeletePage,
    });
  };

  const handleFolderClick = (folder) => {
    setSelectedFolder(folder?._id === selectedFolder ? null : folder?._id);
    setSelectedDesigns([]);
    setSelectAll(false);
    setSearchText("");
    setHeadwallType("all");
    setPage(0);
    searchMutation.mutate({
      page: 0,
      searchText: searchText.trim(),
      model: headwall_type,
      limit: 15,
      folder_id: folder?._id,
      is_trashed: isDeletePage,
    });
  };

  const handleBack = () => {
    setSelectedFolder(null);
    setSelectAll(false);
    setSelectedDesigns([]);
    setPage(0);
    setSearchText("");
    setHeadwallType("all");
    searchMutation.mutate({
      page: 0,
      searchText: searchText.trim(),
      model: headwall_type,
      limit: 15,
      folder_id: "",
      is_trashed: isDeletePage,
    });
  };

  const menuItems = [
    {
      name: "Rename Folder",
      action: handleRename,
      icon: RenameIcon,
      type: "dashboard",
    },
    {
      name: "Download Design",
      action: handleDownloadFolderZip,
      icon: MenuDownloadIcon,
      type: "dashboard",
    },
    {
      name: "Delete Folder",
      action: handleDelete,
      icon: MenuDeleteIcon,
      type: "dashboard",
    },
    {
      name: "Restore Folder",
      action: _handleRestoreItems,
      icon: RestoreFoldrIcon,
      type: "delete",
    },
    {
      name: "Delete Folder",
      action: handleDelete,
      icon: MenuDeleteIcon,
      type: "delete",
    },
  ];

  const designMenusItems = [
    {
      name: "View Design",
      action: handleViewDesign,
      icon: ViewIcon,
    },
    {
      name: "Edit Design",
      action: _handleEditDesign,
      icon: EditIcon,
    },
    {
      name: "Download Desgin",
      action: _handleDownloadDesign,
      icon: DownloadIcon,
    },
  ];

  const getFilteredMenuItems = () => {
    return isDeletePage ? menuItems.slice(3) : menuItems.slice(0, 3);
  };

  const folderName = useMemo(() => {
    const folder = folders?.find((folder) => folder._id === selectedFolder);
    return folder?.name;
  }, [selectedFolder, folders]);

  return (
    <div className="w-100 d-inline-block dashboardMain px-[50px]">
      {!showImage ? (
        <>
          <div className="sticky z-10 bg-white top-[-2px] my-5 py-5">
            <div
              className="headTitleDiv w-100"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "2px",
              }}
            >
              {!selectedFolder && <h2>{heading}</h2>}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  justifyContent: "flex-end",
                }}
              >
                <div style={{ flex: 1, maxWidth: "152px  " }}>
                  <Select
                    name="headwall_type"
                    className={`bg-white rounded-r font-bold text-xs w-full p-2 rounded border border-slate-300 h-[38px] ${
                      headwall_type ? "text-black" : "text-gray-400"
                    }`}
                    options={heawallType}
                    value={headwall_type}
                    handleSelect={(e) => {
                      setHeadwallType(e.target.value);
                      setPage(0);
                    }}
                  />
                </div>
                <div
                  className="search_input flex items-center"
                  style={{ flex: 1, maxWidth: "348px  " }}
                >
                  <Input
                    type="text"
                    name="hsi_contact"
                    placeholder="Search by Design Name"
                    className="w-full rounded px-2 bg-transparent"
                    value={searchText}
                    handleChange={(e) => {
                      _handleSearch(e);
                    }}
                  />
                  {searchText && (
                    <span
                      className="pr-3 cursor-pointer"
                      onClick={handleClearSearch}
                    >
                      X
                    </span>
                  )}
                  <span className="pr-3">
                    <img src={SearchIcon} alt="Person Icon" />
                  </span>
                </div>
                {isDeletePage ? (
                  <>
                    {(!!folders.length || !!designs.length) && (
                      <Tooltip
                        class_name={"cursor-pointer"}
                        icon={DiscardIcon}
                        width={40}
                        height={40}
                        title={"Empty Trash"}
                        handleClick={_handleClearTrash}
                      />
                    )}
                  </>
                ) : (
                  <div
                    onClick={() => {
                      resetState();
                      navigate(`/editor`);
                    }}
                  >
                    <Button
                      className="newDesignBtn"
                      text="Create New Design"
                      type="button"
                    />
                  </div>
                )}
              </div>
            </div>

            {!!selectedDesigns.length && !isDeletePage && (
              <div className="bg-[#ecf5f6] w-full h-[66px] flex items-center justify-end gap-4 px-5  my-5">
                <Link
                  className="underline font-semibold text-lg"
                  onClick={() => handleRequestCADQuote("cad")}
                >
                  Request CAD
                </Link>
                <Link
                  className="underline font-semibold text-lg"
                  onClick={() => handleRequestCADQuote("quote")}
                >
                  Request Quotation
                </Link>
                <Link
                  className="underline font-semibold text-lg"
                  onClick={() => handleRequestCADQuote("both")}
                >
                  Both CAD & Quotation
                </Link>
                {selectedFolder && (
                  <Tooltip
                    class_name={"cursor-pointer folder-reverse"}
                    icon={FoldersIcon}
                    width={48}
                    height={48}
                    handleClick={handleRemoveFromFolder}
                    title={"Remove from Folder"}
                  />
                )}
                <Tooltip
                  class_name={"cursor-pointer"}
                  icon={FoldersIcon}
                  width={48}
                  height={48}
                  handleClick={() => setShowDialog(true)}
                  title={"Move to Folder"}
                />
                <Tooltip
                  class_name={"cursor-pointer"}
                  icon={DiscardIcon}
                  width={48}
                  height={48}
                  handleClick={(e) => handleDelete(e, "file")}
                  title={"Delete Design(s)"}
                />
              </div>
            )}

            {!!selectedDesigns.length && isDeletePage && (
              <div className="bg-[#ecf5f6] w-full h-[66px] flex items-center justify-end gap-4 px-5  my-5">
                <Tooltip
                  class_name={"cursor-pointer"}
                  icon={RestoreIcon}
                  width={48}
                  height={48}
                  handleClick={_handleRestoreItems}
                  title={"Restore Design(s)"}
                />
                <Tooltip
                  class_name={"cursor-pointer"}
                  icon={DiscardIcon}
                  width={48}
                  height={48}
                  handleClick={(e) => handleDeleteTrashedDesigns(e, "file")}
                  title={"Delete Design(s)"}
                />
              </div>
            )}

            {/* folders */}
            {!selectedFolder && (
              <div className="font-medium text-lg my-2">
                <h6 className="font-semibold text-lg text-black">Folders</h6>
                <>
                  {/* {folders?.length ? ( */}
                  <div className="grid grid-cols-5 gap-5 my-5 ">
                    {!isDeletePage && (
                      <Button
                        className={
                          "flex text-white cursor-pointer rounded font-medium text-base bg-[#459BA5] items-center px-3 py-2  leading-4  justify-center"
                        }
                        text={"Create New Folder"}
                        type={"button"}
                        imgPath={WhiteFolderIcon}
                        imgClass={"h-6 w-8 mr-3"}
                        handleClick={() => setShowCreatefolderDialog(true)}
                      />
                    )}
                    {!!folders.length &&
                      folders.map((folder, i) => (
                        <div
                          onDragOver={handleImageDragOver}
                          onDrop={(e) => handleDropImage(e, folder)}
                          className={`folder-item bg-[#FFE8C9] px-3 py-2 items-center justify-between flex border border-[#FFA01E80] rounded relative cursor-pointer ${
                            selectedFolder === folder?._id
                              ? "border-4 border-[#FF650180]"
                              : ""
                          }`}
                          id={i}
                          aria-expanded="true"
                          aria-haspopup="true"
                          key={i}
                          onClick={() => {
                            handleFolderClick(folder);
                          }}
                        >
                          <div className="flex gap-2 font-bold text-base items-center">
                            <img
                              src={FolderIcon}
                              alt="...img"
                              width={33}
                              height={24}
                            />
                            <span className="break-all leading-4">
                              {folder.name}
                            </span>
                          </div>
                          <div
                            className="p-2"
                            onClick={(e) => toggleMenu(e, folder?._id)}
                          >
                            <img
                              src={HamburderIcon}
                              alt="...img"
                              className="cursor-pointer min-w-[5px]"
                            />
                          </div>
                          <div
                            className={`absolute z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none top-[50px] m-0 ${
                              openFolder === folder?._id ? "" : "hidden"
                            }`}
                            role="menu"
                            aria-orientation="vertical"
                            aria-labelledby="menu-button"
                          >
                            {getFilteredMenuItems().map((m, index) => (
                              <div
                                className="p-2"
                                role="none"
                                key={index}
                                onClick={(e) => m.action(e, folder)}
                              >
                                <div className="flex items-center cursor-pointer">
                                  <img
                                    src={m.icon}
                                    alt="...img"
                                    width={16}
                                    height={16}
                                    className="mx-4"
                                  />
                                  <Link
                                    href="#"
                                    className="block text-sm text-gray-700 w-full"
                                    role="menuitem"
                                    id={index}
                                    key={index}
                                  >
                                    {m.name}
                                  </Link>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                  {isDeletePage && !folders.length && (
                    <div className="items-center flex justify-center flex-col">
                      <div className="font-semibold text-[#459BA5]">
                        No Folder
                      </div>
                    </div>
                  )}
                </>
              </div>
            )}
          </div>
          <div className="font-medium text-lg my-2">
            <div
              className={`flex justify-between  ${
                selectedFolder !== null ? "sticky bg-white py-5" : ""
              } ${selectedDesigns.length ? "top-[185px]" : "top-[76px]"}
              `}
            >
              <div className="flex items-center gap-2 justify-start text-black ">
                {selectedFolder && (
                  <>
                    <Tooltip
                      class_name={"cursor-pointer mr-3"}
                      icon={BackIcon}
                      width={8.73}
                      height={16}
                      handleClick={handleBack}
                      title={"Back"}
                    />
                    <h6 className="font-bold text-lg text-[#459BA5]">
                      {folderName}
                    </h6>
                    {">"}
                  </>
                )}
                <h6 className="font-semibold text-medium">Designs</h6>
              </div>
              {designs.length > 1 && (
                <div className="flex items-center">
                  <label
                    className="mr-2 text-sm font-semibold"
                    htmlFor="select_all"
                  >
                    Select All
                  </label>
                  <input
                    name="select_all"
                    type="checkbox"
                    className="w-5 h-5 text-white bg-white border-2 border-green-500 rounded focus:ring-0 checked:bg-green-500 checked:border-green-500 checked:text-green-500"
                    onChange={() => handleSelectAll(!selectAll)}
                    checked={selectAll}
                  />
                </div>
              )}
            </div>
            {/* designs */}
            {designs.length ? (
              <ul className="designListDiv my-5">
                {designs.map((des) => {
                  return (
                    <li key={des._id}>
                      <div className="flex justify-between items-center">
                        <p className="font-bold text-base text-[#8E5359]">
                          {models[des.model]}
                        </p>
                        <div className="titleTxt">
                          <>
                            {((!isDeletePage && !des.folder_is_trashed) ||
                              (isDeletePage && des.folder_is_trashed)) && (
                              <>
                                {des?.folder_name} {des?.folder_name && "/"}{" "}
                              </>
                            )}
                            {des.name}
                          </>
                        </div>
                      </div>
                      <div
                        draggable={true}
                        onDrag={(e) => handleDragImage(e, des)}
                        className={`imgBox cursor-pointer ${
                          selectedDesigns.some(
                            (selected) => selected._id === des._id
                          )
                            ? "border-4 border-[#FF650180]"
                            : ""
                        }`}
                        onClick={() => handleDesignSelect(des)}
                      >
                        <img
                          style={{ pointerEvents: "none" }}
                          onDrag={(e) => handleDragImage(e, des)}
                          src={des.design_image_url}
                          alt="...img"
                        />
                      </div>
                      <span className="detailsTxt flex items-center">
                        <p>
                          {formatDate(
                            isDeletePage ? des.trashed_at : des.updated_at
                          )}
                          {isDeletePage && (
                            <span className="text-[#EA4D4D] ml-1 font-semibold text-xs">
                              (Will be deleted in {des?.days_until_deletion}{" "}
                              days)
                            </span>
                          )}
                        </p>

                        {!isDeletePage &&
                          designMenusItems.map((d, index) => (
                            <div
                              key={index}
                              onClick={() => {
                                d.action(des);
                              }}
                              className={`${index === 1 ? "mx-4" : ""}`}
                            >
                              <Tooltip
                                class_name={"cursor-pointer"}
                                icon={d.icon}
                                width={20}
                                height={20}
                                title={d.name}
                              />
                            </div>
                          ))}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="designListDiv items-center flex justify-center flex-col">
                <div className="font-semibold text-[40px] text-[#459BA5]">
                  No Designs
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <HeadwallImage
          design={selectedDesigns[0]}
          models={models}
          handleCloseViewDesign={handleCloseViewDesign}
          handleDownloadDesign={handleDownloadDesign}
          handleDeleteDesign={handleDelete}
          handleRequestCADQuote={handleRequestCADQuote}
          handleMoveToFolder={setShowDialog}
          fromFolder={selectedFolder}
          handleRemoveFromFolder={handleRemoveFromFolder}
        />
      )}
      {showDialog && (
        <FolderDialog
          selectedFiles={selectedDesigns}
          handleClose={setShowDialog}
          folders={folders}
          setSelectedDesigns={setSelectedDesigns}
          showImage={showImage}
          fromFolder={selectedFolder}
          handleRenameFolder={handleRenameFolder}
          moveFilesToFolder={moveToFolderMutation}
          setShowCreatefolderDialog={setShowCreatefolderDialog}
        />
      )}
      {showRenamefolderDialog.open && (
        <RenameFolderDialog
          handleClose={setShowRenamefolderDialog}
          folders={folders}
          selectedFolderIndex={showRenamefolderDialog.id}
          handleRenameFolder={handleRenameFolder}
          title={"Rename Folder"}
        />
      )}
      {showCreatefolderDialog && (
        <RenameFolderDialog
          handleClose={setShowCreatefolderDialog}
          handleRenameFolder={handleRenameFolder}
          title={"Create New Folder"}
        />
      )}
      {((!showPagination &&
        selectedFolder !== null &&
        designs.length > 0 &&
        !showImage) ||
        (showPagination && !showImage && designs.length > 0)) && (
        <div className="flex justify-end sticky bottom-0 bg-white">
          <Pagination
            handlePageClick={handlePageClick}
            pageCount={pages}
            page={page}
          />
        </div>
      )}
    </div>
  );
}

export default DesignList;

const FolderDialog = ({
  selectedFiles,
  handleClose,
  folders,
  setSelectedDesigns,
  showImage,
  fromFolder,
  handleRenameFolder,
  moveFilesToFolder,
  setShowCreatefolderDialog,
}) => {
  const [selectedFolderIndex, setSelectedFolderIndex] = useState(null);
  const [isEditable, setIsEditable] = useState(false); // Track if it's editable
  const [selectedFolder, setSelectedFolder] = useState(null);

  const foldersArray = useMemo(() => {
    return folders.filter((f) => f._id !== fromFolder);
  }, [folders, fromFolder]);

  const handleDoubleClick = (index) => {
    setSelectedFolderIndex(index); // Set selected folder index
    setIsEditable(true); // Make folder editable
  };

  const handleBlur = (e) => {
    setIsEditable(false); // Disable editing on blur
    setSelectedFolderIndex(null); // Deselect folder on blur
    handleRenameFolder({ name: e.target.innerText, folderId: selectedFolder });
  };

  const handleNameChange = (e) => {
    const text = e.target.innerText;

    // Enforce the maxLength limit by trimming the text, but do not update the state on every input
    if (text.length > 30) {
      e.target.innerText = text.slice(0, 30); // Trim the content directly
    }
  };

  useEffect(() => {
    if (isEditable && selectedFolderIndex !== null) {
      document.getElementById(`folder-${selectedFolderIndex}`).focus(); // Focus the selected folder
    }
  }, [isEditable, selectedFolderIndex]);

  const handleMove = () => {
    const idsArr = selectedFiles.map((files) => files._id);
    const values = {
      fromFolderId: fromFolder !== null ? fromFolder : "",
      toFolderId: selectedFolder,
      inFolder: fromFolder !== null ? true : false, // true-false
      designs: idsArr,
    };
    moveFilesToFolder.mutate(values);
    if (!showImage) setSelectedDesigns([]);
    handleClose(false);
  };

  return (
    <>
      <div
        className="relative z-10"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        ></div>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div>
                  <div className="flex items-center w-full border-b-2">
                    <div className="flex justify-center w-full">
                      <h3
                        className="text-base font-semibold leading-6 text-gray-900 my-3 "
                        id="modal-title"
                      >
                        Move {selectedFiles?.length}{" "}
                        {selectedFiles.length > 1 ? "Designs" : "Design"}
                      </h3>
                    </div>

                    <img
                      src={CloseIcon}
                      alt=""
                      className="cursor-pointer closeBtnModal"
                      onClick={() => handleClose(false)}
                    />
                  </div>
                  <div className="flex justify-center mt-5">
                    <Button
                      className="bg-[#459BA5] inline-flex justify-center rounded-md  px-3 py-2 text-sm font-semibold text-white shadow-sm w-[322px] h-[40px] items-center"
                      text={"Create New Folder"}
                      handleClick={() => setShowCreatefolderDialog(true)}
                      type={"button"}
                      imgPath={WhiteFolderIcon}
                      imgClass={"h-6 w-6 mr-3"}
                    />
                  </div>
                  <div className="flex justify-center my-5">or</div>
                  <div className="text-base font-bold">
                    Choose Folder to Move Files
                    {!foldersArray.length ? (
                      <div className="flex justify-center font-normal text-lg my-5">
                        “No folders available“
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-5 my-5">
                        {foldersArray.map((folder, i) => (
                          <div
                            className="px-4 py-2  h-[50px] items-center justify-between flex border ring-offset-2 rounded relative cursor-pointer"
                            aria-expanded="true"
                            aria-haspopup="true"
                            key={i}
                            style={{
                              border:
                                selectedFolderIndex === i && isEditable
                                  ? "1px solid black"
                                  : selectedFolder === folder?._id
                                  ? "1px solid #FF650180"
                                  : "", // Highlight selected folder
                              padding: "10px",
                              backgroundColor:
                                selectedFolderIndex === i && isEditable
                                  ? "#f0f0f0"
                                  : selectedFolder === folder?._id
                                  ? "#FFE8C9"
                                  : "", // Optional: add background color for selected folder
                            }}
                            onClick={() => setSelectedFolder(folder?._id)}
                          >
                            <div className="flex gap-3 font-bold text-base w-full">
                              <img
                                src={FolderIcon}
                                alt="...img"
                                width={33}
                                height={24}
                              />
                              <div
                                id={`folder-${i}`}
                                className="customTxt design_name w-full break-all"
                                contentEditable={
                                  isEditable && selectedFolderIndex === i
                                }
                                onDoubleClick={() => handleDoubleClick(i)}
                                onBlur={(e) => handleBlur(e)}
                                suppressContentEditableWarning={true}
                                onKeyDown={(e) => {
                                  const content = e.target.innerText;
                                  // Prevent the "Enter" key
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                  }
                                  // Limit content to maxLength
                                  if (
                                    content.length >= 25 &&
                                    e.key !== "Backspace"
                                  ) {
                                    e.preventDefault();
                                  }
                                }}
                                onInput={handleNameChange}
                              >
                                {folder.name}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:px-6  justify-center gap-10">
                <Button
                  className="inline-flex  justify-center rounded-md  px-3 py-2 text-sm font-semibold text-[#105E60] shadow-sm w-[140px] h-[44px] items-center ring-offset-2 border-[#105E60] border"
                  text={"Cancel"}
                  handleClick={() => handleClose(false)}
                  type={"button"}
                />
                <Button
                  className="login_btn inline-flex  justify-center rounded-md  px-3 py-2 text-sm font-semibold text-white shadow-sm w-[140px] h-[44px] items-center"
                  text={"Move"}
                  handleClick={handleMove}
                  type={"button"}
                  isDisabled={selectedFolder ? false : true}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const RenameFolderDialog = (props) => {
  const {
    handleClose,
    folders,
    selectedFolderIndex,
    handleRenameFolder,
    title,
  } = props;
  const inputRef = useRef(null);
  const [value, setValue] = useState({ name: "", folderId: "" });

  useEffect(() => {
    const folder = folders?.find(
      (folder) => folder?._id === selectedFolderIndex
    );
    setValue({ name: folder?.name, folderId: folder?._id });
    if (inputRef.current) inputRef.current.focus();
  }, [selectedFolderIndex, folders]);

  const handleSubmit = (e) => {
    e.preventDefault();
    handleRenameFolder(value);
  };

  return (
    <>
      <div
        className="relative z-10"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        ></div>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <form onSubmit={handleSubmit}>
              <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md">
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div>
                    <div className="flex items-center w-full border-b-2">
                      <div className="flex justify-center w-11/12">
                        <h3
                          className="text-base font-semibold leading-6 text-gray-900 my-3 "
                          id="modal-title"
                        >
                          {title}
                        </h3>
                      </div>

                      <img
                        src={CloseIcon}
                        alt=""
                        className="cursor-pointer closeBtnModal"
                        onClick={() => handleClose(false)}
                      />
                    </div>
                    <div className="mt-5 mx-10">
                      <div className="login_input flex items-center w-80">
                        <span className="ml-3">
                          <img src={FolderIcon} alt="img" />
                        </span>
                        <Input
                          ref={inputRef}
                          type="text"
                          name="state"
                          placeholder="Please Enter Folder Name"
                          className="ml-4 w-full h-full rounded"
                          value={value.name}
                          handleChange={(e) => {
                            const inputValue = e.target.value;
                            if (inputValue.length <= 30) {
                              setValue({ ...value, name: inputValue });
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:px-6  justify-center gap-5">
                  <Button
                    className="inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-[#105E60] shadow-sm w-[120px] h-[44px] items-center ring-offset-2 border-[#105E60] border"
                    text={"No"}
                    handleClick={() => handleClose(false)}
                    type={"button"}
                  />
                  <Button
                    className="login_btn inline-flex  justify-center rounded-md  px-3 py-2 text-sm font-semibold text-white shadow-sm w-[120px] h-[44px] items-center"
                    text={"Save"}
                    type={"submit"}
                    isDisabled={!value?.name?.trim()}
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
